/**
 * @fileoverview The advice for Today's welcome, as the server placed it: the
 * postcode step (reads.postcode_step, shown by TodayPostcode) and the first
 * step after it (reads.empty_garden: while one is placed, Today draws
 * WelcomePlace, its two ways to start (B96), in place of the advice's own words
 * and button). The server decides when each is due (no postcode; an empty
 * garden, or a new account until it adds a plant of its own).
 *
 * Where: TodayPage (useFirstStep for the welcome's first step,
 * usePostcodeTip for the ZIP step's words).
 * API: none of its own; seen goes through TipsContext.
 * Words: the advice's own; Today falls back to its own strings when the
 * postcode advice is switched off.
 *
 * @module tips/useFirstStep
 */

import { useTips } from './useTips';
import { useSeen } from './TipViews';
import type { Advice, TipWords } from './types';

/**
 * The first-step advice the server placed for this garden, said seen while it is drawn.
 *
 * @param show - Whether Today is ready to draw it (the postcode is in and the plants have loaded).
 * @returns The advice and its words, or nulls when none is placed.
 */
export function useFirstStep(show: boolean): { tip: Advice | null; words: TipWords | null } {
  const tips = useTips();
  const tip = show ? tips.read('empty_garden') : null;
  useSeen(tip);
  return { tip, words: tip ? tips.words(tip) : null };
}

/**
 * The postcode step's advice, for a garden without a postcode, said seen while
 * it is drawn: its title is Today's heading, its line and button are in the
 * box. Null until the advice has arrived, or when it is switched off; Today
 * then says the same words from the app's own strings.
 *
 * @param noPostcode - Whether the garden has no postcode yet.
 * @returns The advice and its words, or nulls.
 */
export function usePostcodeTip(noPostcode: boolean): { tip: Advice | null; words: TipWords | null } {
  const tips = useTips();
  const tip = noPostcode ? tips.read('postcode_step') : null;
  useSeen(tip);
  return { tip, words: tip ? tips.words(tip) : null };
}
